<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { listUsers, freezeUser, unfreezeUser, banUser, unbanUser, type UserRow } from '../api';

const total = ref(0);
const items = ref<UserRow[]>([]);
const loading = ref(false);
const statusFilter = ref<string>('');
const qFilter = ref('');
const includeBanned = ref(false);
const page = ref(1);
const pageSize = 20;

async function reload() {
  loading.value = true;
  try {
    const r = await listUsers({
      status: statusFilter.value || undefined,
      q: qFilter.value || undefined,
      take: pageSize,
      skip: (page.value - 1) * pageSize,
      includeBanned: includeBanned.value,
    });
    total.value = r.total;
    items.value = r.items;
  } catch (e) {
    ElMessage.error(normalize(e));
  } finally {
    loading.value = false;
  }
}

async function onFreeze(row: UserRow) {
  try {
    const { value } = await ElMessageBox.prompt('冻结原因(可留空)', `冻结 ${row.openid}`, {
      inputType: 'textarea',
      confirmButtonText: '冻结',
      cancelButtonText: '取消',
      inputValidator: () => true,
    });
    await freezeUser(row.id, value);
    ElMessage.success('已冻结');
    reload();
  } catch (e) {
    if ((e as { message?: string })?.message === 'cancel') return;
    ElMessage.error(normalize(e));
  }
}

async function onUnfreeze(row: UserRow) {
  try {
    await ElMessageBox.confirm(`确认解冻 ${row.openid}?`, '解冻', { type: 'warning' });
    await unfreezeUser(row.id);
    ElMessage.success('已解冻');
    reload();
  } catch (e) {
    if (e === 'cancel' || (e as { message?: string })?.message === 'cancel') return;
    ElMessage.error(normalize(e));
  }
}

async function onBan(row: UserRow) {
  try {
    const { value } = await ElMessageBox.prompt(
      `⚠ 封号操作不可简单撤销(软删 + 撤所有角色)\n请填写封号原因(必填,记入日志):`,
      `封号 ${row.openid}`,
      {
        inputType: 'textarea',
        confirmButtonText: '确认封号',
        cancelButtonText: '取消',
        inputValidator: (v) => (v && v.trim().length >= 2 ? true : '至少 2 字'),
      },
    );
    await banUser(row.id, value.trim());
    ElMessage.success('已封号');
    reload();
  } catch (e) {
    if ((e as { message?: string })?.message === 'cancel') return;
    ElMessage.error(normalize(e));
  }
}

async function onUnban(row: UserRow) {
  try {
    await ElMessageBox.confirm(
      `确认解封 ${row.openid}?解封后账号恢复为「待审核」,需重新认证拿回圈友身份。`,
      '解封',
      { type: 'warning', confirmButtonText: '确认解封' },
    );
    await unbanUser(row.id);
    ElMessage.success('已解封');
    reload();
  } catch (e) {
    if (e === 'cancel' || (e as { message?: string })?.message === 'cancel') return;
    ElMessage.error(normalize(e));
  }
}

function statusTag(s: string) {
  const map: Record<string, { type: 'info' | 'success' | 'warning' | 'danger'; label: string }> = {
    PENDING: { type: 'warning', label: '待审核' },
    APPROVED: { type: 'success', label: '已认证' },
    REJECTED: { type: 'danger', label: '审核驳回' },
    FROZEN: { type: 'info', label: '已冻结' },
  };
  return map[s] ?? { type: 'info' as const, label: s };
}

function normalize(e: unknown) {
  const err = e as { message?: string; code?: string };
  return err?.message || err?.code || '未知错误';
}

function formatTime(s: string | null): string {
  if (!s) return '-';
  return new Date(s).toLocaleString('zh-CN');
}

watch([statusFilter, qFilter], () => {
  page.value = 1;
  reload();
});

onMounted(reload);
</script>

<template>
  <div class="users-pane">
    <div class="filters">
      <el-select v-model="statusFilter" placeholder="状态" clearable style="width: 140px">
        <el-option label="待审核" value="PENDING" />
        <el-option label="已认证" value="APPROVED" />
        <el-option label="审核驳回" value="REJECTED" />
        <el-option label="已冻结" value="FROZEN" />
      </el-select>
      <el-input
        v-model="qFilter"
        placeholder="搜 openid / 昵称 / 学校"
        clearable
        style="width: 280px; margin-left: 8px"
      />
      <el-checkbox v-model="includeBanned" style="margin-left: 12px" @change="reload">含已封号</el-checkbox>
      <el-button @click="reload" style="margin-left: 8px">刷新</el-button>
      <span class="total">共 {{ total }} 条</span>
    </div>

    <el-table :data="items" v-loading="loading" stripe>
      <el-table-column prop="openid" label="openid" width="180" />
      <el-table-column prop="displayName" label="昵称" width="140">
        <template #default="{ row }">{{ row.displayName || '-' }}</template>
      </el-table-column>
      <el-table-column prop="schoolName" label="学校" width="140">
        <template #default="{ row }">{{ row.schoolName || '-' }}</template>
      </el-table-column>
      <el-table-column prop="enrollYear" label="届" width="70">
        <template #default="{ row }">{{ row.enrollYear || '-' }}</template>
      </el-table-column>
      <el-table-column label="状态" width="100">
        <template #default="{ row }">
          <el-tag :type="statusTag(row.status).type" size="small">
            {{ statusTag(row.status).label }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="角色" width="160">
        <template #default="{ row }">
          <el-tag
            v-for="r of row.roles"
            :key="r"
            size="small"
            style="margin-right: 4px"
          >{{ r }}</el-tag>
          <span v-if="row.roles.length === 0" class="muted">-</span>
        </template>
      </el-table-column>
      <el-table-column label="邀请人数" width="100" align="center">
        <template #default="{ row }">
          <el-tag v-if="row.invitedCount > 0" type="success" size="small">{{ row.invitedCount }}</el-tag>
          <span v-else class="muted">0</span>
        </template>
      </el-table-column>
      <el-table-column label="注册" width="160">
        <template #default="{ row }">{{ formatTime(row.createdAt) }}</template>
      </el-table-column>
      <el-table-column label="操作" fixed="right" width="220">
        <template #default="{ row }">
          <!-- 已封号(软删):仅显示解封 -->
          <template v-if="row.deletedAt">
            <el-tag type="danger" size="small" style="margin-right: 8px">已封号</el-tag>
            <el-button size="small" type="success" @click="onUnban(row)">解封</el-button>
          </template>
          <template v-else>
            <el-button
              v-if="row.status !== 'FROZEN'"
              size="small"
              @click="onFreeze(row)"
            >冻结</el-button>
            <el-button
              v-else
              size="small"
              type="warning"
              @click="onUnfreeze(row)"
            >解冻</el-button>
            <el-button
              size="small"
              type="danger"
              @click="onBan(row)"
            >封号</el-button>
          </template>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-model:current-page="page"
      :page-size="pageSize"
      :total="total"
      layout="prev, pager, next, total"
      @current-change="reload"
      style="margin-top: 16px; justify-content: flex-end"
    />
  </div>
</template>

<style scoped>
.users-pane { padding: 4px 0; }
.filters { display: flex; align-items: center; margin-bottom: 16px; }
.total { margin-left: auto; color: #86868b; font-size: 13px; }
.muted { color: #86868b; }
</style>
